import { Injectable } from '@angular/core';
import { UI_PREFERENCES_STORAGE_KEY } from './preserved-storage-keys';

/** Persisted UI preferences: one boolean flag per key. */
type IUiPreferences = Record<string, boolean>;

/**
 * Known preference keys. Declared here so the persisted shape stays
 * discoverable instead of being spelled out as magic strings in components.
 */
export const UI_PREFERENCE_KEYS = {
  /** "Return to the order list after confirmation" checkbox of the send-to-ERP dialog. */
  RETURN_TO_LIST_AFTER_ORDER_CONFIRMATION: 'returnToListAfterOrderConfirmation',
} as const;

/**
 * Single source of truth for standalone (non-table) user UI preferences,
 * persisted to `localStorage` under one shared key.
 *
 * Persistence is intentionally localStorage-only for now; all reads/writes are
 * funnelled through this service so the storage tier can be swapped for a
 * user-profile API later without touching any component. The storage key lives in
 * `preserved-storage-keys` so it survives a logout.
 */
@Injectable({ providedIn: 'root' })
export class UiPreferencesService {

  /** Persisted flag, or `fallback` when never set / storage unavailable. */
  getFlag(key: string, fallback = false): boolean {
    const value = this.read()[key];
    return typeof value === 'boolean' ? value : fallback;
  }

  setFlag(key: string, value: boolean): void {
    this.write({ ...this.read(), [key]: value });
  }

  private read(): IUiPreferences {
    try {
      const raw = localStorage.getItem(UI_PREFERENCES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        return parsed as IUiPreferences;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return {};
  }

  private write(preferences: IUiPreferences): void {
    try {
      localStorage.setItem(UI_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // ignore quota / disabled storage errors — preferences are best-effort
    }
  }
}
